// 치과 「예약」 /patients/bookings (2026-10-11 — 계획 docs/계획_케어앱_예약대기_2026-10-11.md W1, 대표 10-11 승인)
//   탭 4개: 요청(새 N) / 일정(일·주) / 빈자리 / 설정(대표만 활성).
//   원칙(계획 §9 직원 손 최소): 확정 = 희망 클릭 1번 + [확정] · 빈자리 = 날짜·시간 2칸 + [보내기](대상 자동, 문구 고정) · 확인 창 최소.
//   금지(법무 §1·C-3·C-4): 자유 문구 입력란·할인/재촉/긴급 표현·결제. 환자에게 가는 문구는 서버 고정.
//   게이트: App.js 라우트(치과 && isCareBookingOn) + 이 화면에서 서버 available(파일럿·위탁 판) 확인 — false 면 데이터 호출 0.
//   서버 계약: services/bookingApi.js 한 곳(계약 문서 작성 전 계획서 이름으로 가정 — bookingApi 머리 주석).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, ArrowLeft, RefreshCw, Inbox, CalendarDays, BellRing, Settings as SettingsIcon } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { unifiedStyles as U } from '../../styles/Styles';
import { useBookingAvailable } from '../../utils/useBookingAvailable';
import {
    BOOKING_HEAD_NOTE, OFFER_FIXED_NOTE, EMR_NOTE, CHART_NOTED_LABEL, SETTINGS_LOCK_NOTE, offerConfirmText, offerAppText, reasonLabel, partLabel, DEFAULT_TIME_OF_PART, DECLINE_REASONS, declineLabel,
    statusLabel, offerStatusLabel, OFFER_WINDOW_OPTIONS, DAILY_CAP_MAX, WEEKDAYS, dayKeyOf, addDays, fmtDay, fmtMs, isTime, isDayKey,
    routeBadgeOf, describeBookingError,
    listClinicBookingRequests, decideClinicBooking, setClinicBookingChartNoted, listClinicBookingSchedule,
    sendClinicBookingOffer, previewClinicBookingOffer, listClinicBookingOffers, closeClinicBookingOffer, getClinicBookingInboxCount, setClinicBookingSettings,
} from '../../services/bookingApi';

const OWNER_ONLY_TEXT = '치과 대표만 바꿀 수 있습니다. (직원 계정은 보기만 가능)';
const errBox = { background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8, lineHeight: 1.5 };
const okBox = { background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8, lineHeight: 1.5 };
const infoBox = { background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, lineHeight: 1.6 };
const input = { padding: '7px 9px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' };
const sub = { fontSize: 12.5, color: '#64748b' };
const chipBtn = (active) => ({ ...U.buttonSecondary, padding: '5px 12px', fontSize: 13, background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#334155', borderColor: active ? '#4f46e5' : '#e2e8f0' });
const smallBtn = { ...U.buttonSecondary, padding: '5px 10px', fontSize: 12.5 };
const smallPri = { ...U.buttonPrimary, padding: '5px 12px', fontSize: 12.5 };
const tag = (bg, fg) => ({ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap', background: bg, color: fg });
const disabledStyle = (d) => (d ? { opacity: 0.55, cursor: 'not-allowed' } : {});

// 연타 가드 — state 는 다음 렌더 전까지 반영되지 않으므로 ref 로 즉시 잠근다
function useInFlight() {
    const ref = useRef(false);
    return useCallback(async (fn) => {
        if (ref.current) return undefined;
        ref.current = true;
        try { return await fn(); } finally { ref.current = false; }
    }, []);
}

// 차트 반영 토글(직원 표시용 — 선택) — 요청 탭 "방금 확정"과 일정 탭에서 같이 쓴다
function ChartNotedToggle({ booking, onChanged }) {
    const [val, setVal] = useState(booking.chartNoted);
    const [err, setErr] = useState('');
    const guard = useInFlight();
    useEffect(() => { setVal(booking.chartNoted); }, [booking.chartNoted]);
    const toggle = () => guard(async () => {
        const next = !val;
        setVal(next); setErr('');
        try { await setClinicBookingChartNoted({ pid: booking.pid, bookingId: booking.bookingId, chartNoted: next }); if (onChanged) onChanged(next); }
        catch (e) { setVal(!next); setErr(describeBookingError(e)); }
    });
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: val ? '#047857' : '#334155', cursor: 'pointer' }}>
                <input type="checkbox" checked={val} onChange={toggle} />
                {CHART_NOTED_LABEL}
            </label>
            {err && <span style={{ fontSize: 12, color: '#b91c1c' }}>{err}</span>}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 요청 탭 — 행 1개 (확정 · 다른 시간 제안 · 전화로 안내 · 받지 못함)
// ─────────────────────────────────────────────────────────────────────────────
function RequestRow({ b, onDone }) {
    const firstWish = b.wishes[0] || null;
    const [pick, setPick] = useState(firstWish ? 0 : -1);
    const [time, setTime] = useState(firstWish ? DEFAULT_TIME_OF_PART[firstWish.part] : '10:00');
    const [mode, setMode] = useState('');   // '' | 'propose' | 'decline'
    const [pDay, setPDay] = useState(firstWish ? firstWish.day : addDays(dayKeyOf(), 1));
    const [pTime, setPTime] = useState('10:00');
    const [declineKey, setDeclineKey] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const guard = useInFlight();

    const choose = (i) => { setPick(i); setTime(DEFAULT_TIME_OF_PART[b.wishes[i].part] || '10:00'); setMode(''); setErr(''); };
    const run = (action, extra) => guard(async () => {
        setBusy(true); setErr('');
        try { await decideClinicBooking({ pid: b.pid, bookingId: b.bookingId, action, ...extra }); onDone(action, b, extra?.slot || null); }
        catch (e) { setErr(describeBookingError(e)); }
        finally { setBusy(false); }
    });
    const confirm = () => {
        const w = b.wishes[pick];
        if (!w) { setErr('희망 날짜를 먼저 누르세요.'); return; }
        if (!isTime(time)) { setErr('시각을 확인해 주세요.'); return; }
        run('confirm', { slot: { day: w.day, time } });
    };
    const propose = () => {
        if (!isDayKey(pDay) || pDay < dayKeyOf()) { setErr('제안 날짜는 오늘 이후로 골라 주세요.'); return; }
        if (!isTime(pTime)) { setErr('시각을 확인해 주세요.'); return; }
        run('propose', { slot: { day: pDay, time: pTime } });
    };
    const decline = () => { if (!declineKey) { setErr('사유를 골라 주세요.'); return; } run('decline', { declineReason: declineKey }); };

    const badge = routeBadgeOf(b);
    return (
        <div style={{ borderTop: '1px solid #f1f5f9', padding: '10px 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <b style={{ fontSize: 14, color: '#0f172a' }}>{b.name || '-'}</b>
                {badge && <span style={tag('#eef2ff', '#4338ca')}>{badge}</span>}
                {b.waitlist && <span style={tag('#f1f5f9', '#475569')}>{'빈자리 대기'}</span>}
                <span style={sub}>{`방문 이유 ${reasonLabel(b.reason)}`}</span>
                <span style={{ ...sub, marginLeft: 'auto' }}>{b.createdAt ? `요청 ${fmtMs(b.createdAt)}` : ''}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={sub}>{'희망'}</span>
                {b.wishes.map((w, i) => (
                    <button key={`${w.day}-${w.part}-${i}`} type="button" disabled={busy} onClick={() => choose(i)} style={chipBtn(pick === i && mode === '')}>
                        {`${fmtDay(w.day)} ${partLabel(w.part)}`}
                    </button>
                ))}
                {pick >= 0 && mode === '' && (
                    <input type="time" step={600} value={time} onChange={(e) => setTime(e.target.value)} style={{ ...input, width: 110 }} aria-label="확정 시각" />
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" disabled={busy || pick < 0} onClick={confirm} style={{ ...smallPri, ...disabledStyle(busy || pick < 0) }}>{'확정'}</button>
                <button type="button" disabled={busy} onClick={() => { setMode(mode === 'propose' ? '' : 'propose'); setErr(''); }} style={chipBtn(mode === 'propose')}>{'다른 시간 제안'}</button>
                <button type="button" disabled={busy} onClick={() => run('phone')} style={smallBtn}>{'전화로 안내'}</button>
                <select value={mode === 'decline' ? declineKey : ''} disabled={busy}
                    onChange={(e) => { setDeclineKey(e.target.value); setMode(e.target.value ? 'decline' : ''); setErr(''); }}
                    style={{ ...input, padding: '5px 8px' }} aria-label="받지 못함 사유">
                    <option value="">{'받지 못함'}</option>
                    {DECLINE_REASONS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
                {mode === 'decline' && declineKey && (
                    <button type="button" disabled={busy} onClick={decline} style={{ ...smallBtn, color: '#b91c1c', borderColor: '#fecaca' }}>{'받지 못함'}</button>
                )}
                {busy && <span style={sub}>{'처리 중…'}</span>}
            </div>
            {mode === 'propose' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    <input type="date" value={pDay} min={dayKeyOf()} onChange={(e) => setPDay(e.target.value)} style={input} aria-label="제안 날짜" />
                    <input type="time" step={600} value={pTime} onChange={(e) => setPTime(e.target.value)} style={{ ...input, width: 110 }} aria-label="제안 시각" />
                    <button type="button" disabled={busy} onClick={propose} style={smallPri}>{'제안 보내기'}</button>
                </div>
            )}
            {err && <div style={errBox}>{err}</div>}
        </div>
    );
}

// 진행 중·끝난 요청 — 읽기 전용 행
function StatusRow({ b }) {
    const badge = routeBadgeOf(b);
    const detail = b.status === 'confirmed' && b.slot ? `${fmtDay(b.slot.day)} ${b.slot.time}`
        : b.status === 'proposed' && b.proposed ? `제안 ${fmtDay(b.proposed.day)} ${b.proposed.time}`
        : b.status === 'declined' ? declineLabel(b.declineReason)
        : b.status === 'cancelled' ? (b.cancelledBy === 'patient' ? '환자가 취소' : b.cancelledBy === 'clinic' ? '치과가 취소' : '')
        : '';
    return (
        <div style={{ borderTop: '1px solid #f1f5f9', padding: '9px 4px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <b style={{ fontSize: 14, color: '#0f172a' }}>{b.name || '-'}</b>
            {badge && <span style={tag('#eef2ff', '#4338ca')}>{badge}</span>}
            {b.waitlist && <span style={tag('#f1f5f9', '#475569')}>{'빈자리 대기'}</span>}
            <span style={sub}>{reasonLabel(b.reason)}</span>
            <span style={tag('#f8fafc', '#334155')}>{statusLabel(b.status)}</span>
            {detail && <span style={sub}>{detail}</span>}
            <span style={{ ...sub, marginLeft: 'auto' }}>{b.updatedAt ? fmtMs(b.updatedAt) : b.createdAt ? fmtMs(b.createdAt) : ''}</span>
        </div>
    );
}

const REQ_FILTERS = [
    { key: 'new', label: '새 요청' },
    { key: 'open', label: '진행 중' },
    { key: 'done', label: '끝난 요청' },
];

function RequestsTab({ onCountChange }) {
    const [filter, setFilter] = useState('new');
    const [state, setState] = useState({ loading: true, items: [], err: '', nextCursor: null });
    const [more, setMore] = useState({ busy: false, err: '' });   // [더 보기] — 서버 50건씩(계약 2-3 nextCursor)
    const [justConfirmed, setJustConfirmed] = useState([]);   // 방금 확정한 건 — EMR 안내 + [차트 반영] (목록에서는 빠지므로 따로 보인다)
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        setState(s => ({ ...s, loading: true, err: '' }));
        setMore({ busy: false, err: '' });
        try { const r = await listClinicBookingRequests({ status: filter }); setState({ loading: false, items: r.items, err: '', nextCursor: r.nextCursor }); }
        catch (e) { setState({ loading: false, items: [], err: describeBookingError(e, '예약 요청을 불러오지 못했습니다.'), nextCursor: null }); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);
    const loadMore = async () => {
        if (more.busy || state.nextCursor == null) return;
        setMore({ busy: true, err: '' });
        try {
            const r = await listClinicBookingRequests({ status: filter, cursor: state.nextCursor });
            setState(s => {
                const seen = new Set(s.items.map(x => x.bookingId));
                return { ...s, items: [...s.items, ...r.items.filter(x => !seen.has(x.bookingId))], nextCursor: r.nextCursor };
            });
            setMore({ busy: false, err: '' });
        } catch (e) { setMore({ busy: false, err: describeBookingError(e, '더 불러오지 못했습니다.') }); }
    };

    const onDone = (action, b, slot) => {
        if (action === 'confirm') { setJustConfirmed(list => [{ ...b, status: 'confirmed', slot, chartNoted: false }, ...list].slice(0, 10)); setNotice(''); }
        else setNotice(action === 'propose' ? `${b.name} 님께 다른 시간을 제안했습니다.` : action === 'phone' ? `${b.name} 님 요청을 「전화로 안내」로 처리했습니다. 환자 앱에는 전화로 안내한다는 표시가 나갑니다. 환자에게 전화해 주세요.` : `${b.name} 님 요청을 받지 못함으로 처리했습니다.`);
        load();
        onCountChange();
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                {REQ_FILTERS.map(f => <button key={f.key} type="button" onClick={() => setFilter(f.key)} style={chipBtn(filter === f.key)}>{f.label}</button>)}
                <button type="button" onClick={() => { load(); onCountChange(); }} style={{ ...smallBtn, marginLeft: 'auto' }}><RefreshCw size={13} /> {'새로고침'}</button>
            </div>
            {justConfirmed.length > 0 && (
                <div style={{ ...okBox, marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{EMR_NOTE}</div>
                    {justConfirmed.map(b => (
                        <div key={b.bookingId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '3px 0' }}>
                            <span>{`${b.name} · ${b.slot ? `${fmtDay(b.slot.day)} ${b.slot.time}` : ''} 확정`}</span>
                            <ChartNotedToggle booking={b} onChanged={(v) => setJustConfirmed(list => list.map(x => (x.bookingId === b.bookingId ? { ...x, chartNoted: v } : x)))} />
                        </div>
                    ))}
                </div>
            )}
            {notice && <div style={{ ...infoBox, marginBottom: 10 }}>{notice}</div>}
            {state.loading ? <div style={{ ...U.emptyState, padding: '24px 0' }}>{'불러오는 중...'}</div>
                : state.err ? <div style={errBox}>{state.err}</div>
                : state.items.length === 0 ? <div style={{ ...U.emptyState, padding: '24px 0' }}>{filter === 'new' ? '새 예약 요청이 없습니다.' : '해당하는 요청이 없습니다.'}</div>
                : filter === 'new'
                    ? state.items.map(b => <RequestRow key={b.bookingId} b={b} onDone={onDone} />)
                    : state.items.map(b => <StatusRow key={b.bookingId} b={b} />)}
            {!state.loading && !state.err && state.nextCursor != null && (
                <div style={{ textAlign: 'center', paddingTop: 10 }}>
                    <button type="button" disabled={more.busy} onClick={loadMore} style={smallBtn}>{more.busy ? '불러오는 중...' : '더 보기'}</button>
                    {more.err && <div style={errBox}>{more.err}</div>}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 일정 탭 — 확정 목록(일·주). 체어·담당자 칸 없음(EMR 이 정본)
// ─────────────────────────────────────────────────────────────────────────────
function ScheduleTab() {
    const [view, setView] = useState('day');   // 'day' | 'week'
    const [base, setBase] = useState(dayKeyOf());
    const [state, setState] = useState({ loading: true, items: [], err: '' });
    const [rowErr, setRowErr] = useState({});
    const guard = useInFlight();
    const from = base;
    const to = view === 'day' ? base : addDays(base, 6);

    const load = useCallback(async () => {
        setState(s => ({ ...s, loading: true, err: '' }));
        try { const r = await listClinicBookingSchedule({ from, to }); setState({ loading: false, items: r.items, err: '' }); }
        catch (e) { setState({ loading: false, items: [], err: describeBookingError(e, '일정을 불러오지 못했습니다.') }); }
    }, [from, to]);
    useEffect(() => { load(); }, [load]);

    const cancel = (b) => guard(async () => {
        if (!window.confirm(`${b.name} 님 ${fmtDay(b.slot?.day)} ${b.slot?.time || ''} 예약을 취소할까요? 환자 앱 「내 일정」에 바로 표시됩니다. 환자에게 전화로도 알려 주세요.`)) return;
        setRowErr(m => ({ ...m, [b.bookingId]: '' }));
        try { await decideClinicBooking({ pid: b.pid, bookingId: b.bookingId, action: 'cancel' }); load(); }
        catch (e) { setRowErr(m => ({ ...m, [b.bookingId]: describeBookingError(e) })); }
    });

    const byDay = useMemo(() => {
        const m = {};
        state.items.forEach(b => { const k = b.slot?.day || ''; (m[k] = m[k] || []).push(b); });
        Object.values(m).forEach(list => list.sort((a, c) => String(a.slot?.time || '').localeCompare(String(c.slot?.time || ''))));
        return Object.keys(m).sort().map(k => [k, m[k]]);
    }, [state.items]);

    const step = view === 'day' ? 1 : 7;
    return (
        <div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                <button type="button" onClick={() => setView('day')} style={chipBtn(view === 'day')}>{'일'}</button>
                <button type="button" onClick={() => setView('week')} style={chipBtn(view === 'week')}>{'주'}</button>
                <button type="button" onClick={() => setBase(addDays(base, -step))} style={smallBtn}>{'‹'}</button>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{view === 'day' ? fmtDay(from) : `${fmtDay(from)} ~ ${fmtDay(to)}`}</span>
                <button type="button" onClick={() => setBase(addDays(base, step))} style={smallBtn}>{'›'}</button>
                <button type="button" onClick={() => setBase(dayKeyOf())} style={smallBtn}>{'오늘'}</button>
                <button type="button" onClick={load} style={{ ...smallBtn, marginLeft: 'auto' }}><RefreshCw size={13} /> {'새로고침'}</button>
            </div>
            {state.loading ? <div style={{ ...U.emptyState, padding: '24px 0' }}>{'불러오는 중...'}</div>
                : state.err ? <div style={errBox}>{state.err}</div>
                : byDay.length === 0 ? <div style={{ ...U.emptyState, padding: '24px 0' }}>{'확정된 예약이 없습니다.'}</div>
                : byDay.map(([day, list]) => (
                    <div key={day} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', padding: '6px 4px' }}>{`${fmtDay(day)} · ${list.length}건`}</div>
                        {list.map(b => (
                            <div key={b.bookingId} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 4px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontFamily: 'monospace', fontSize: 13.5, fontWeight: 700, color: '#0f172a', minWidth: 46 }}>{b.slot?.time || '-'}</span>
                                <b style={{ fontSize: 14, color: '#0f172a' }}>{b.name || '-'}</b>
                                <span style={sub}>{reasonLabel(b.reason)}</span>
                                {b.movedFrom && <span style={tag('#fff7ed', '#9a3412')}>{`옮김 · 원래 ${fmtDay(b.movedFrom.day)} ${b.movedFrom.time}`}</span>}
                                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                    <ChartNotedToggle booking={b} />
                                    <button type="button" onClick={() => cancel(b)} style={{ ...smallBtn, color: '#b91c1c', borderColor: '#fecaca' }}>{'취소'}</button>
                                </span>
                                {rowErr[b.bookingId] && <div style={{ ...errBox, flexBasis: '100%' }}>{rowErr[b.bookingId]}</div>}
                            </div>
                        ))}
                    </div>
                ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 빈자리 탭 — [빈자리 알리기] 날짜·시간 2칸 → 대상 미리보기("대기 중 N명에게 알립니다") → 보내기. 문구 고정(C-4), 대상 = 대기 신청자만(C-5)
// ─────────────────────────────────────────────────────────────────────────────
function OfferTab({ clinicName, offerWindowMin }) {
    const [day, setDay] = useState(dayKeyOf());
    const [time, setTime] = useState('');
    const [preview, setPreview] = useState({ loading: false, targetCount: 0, targets: [], err: '', key: '' });
    const [showNames, setShowNames] = useState(false);
    const [excluded, setExcluded] = useState([]);   // 이름 목록에서 뺀 bookingId (기본 전원)
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [ok, setOk] = useState('');
    const [offers, setOffers] = useState({ loading: true, items: [], err: '' });
    const [offerErr, setOfferErr] = useState({});
    const guard = useInFlight();

    const loadOffers = useCallback(async () => {
        setOffers(s => ({ ...s, loading: true, err: '' }));
        try { const r = await listClinicBookingOffers(); setOffers({ loading: false, items: r.items, err: '' }); }
        catch (e) { setOffers({ loading: false, items: [], err: describeBookingError(e, '보낸 빈자리를 불러오지 못했습니다.') }); }
    }, []);
    useEffect(() => { loadOffers(); }, [loadOffers]);

    const slotKey = isDayKey(day) && isTime(time) ? `${day} ${time}` : '';
    // 대상 미리보기 — 날짜·시간이 다 들어오면 0.6초 뒤 서버 dryRun 1회(계약 2-6, 접속기록 booking_offer_preview). 대상 판정은 서버가 정본
    useEffect(() => {
        setExcluded([]); setShowNames(false);
        if (!slotKey) { setPreview({ loading: false, targetCount: 0, targets: [], err: '', key: '' }); return undefined; }
        let alive = true;
        setPreview(p => ({ ...p, loading: true, err: '' }));
        const t = setTimeout(async () => {
            try {
                const r = await previewClinicBookingOffer({ day, time });
                if (alive) setPreview({ loading: false, targetCount: r.targetCount, targets: r.targets, err: '', key: slotKey });
            } catch (e) {
                if (alive) setPreview({ loading: false, targetCount: 0, targets: [], err: describeBookingError(e, '대기 환자를 확인하지 못했습니다.'), key: slotKey });
            }
        }, 600);
        return () => { alive = false; clearTimeout(t); };
    }, [slotKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const ready = !!slotKey && preview.key === slotKey && !preview.loading && !preview.err;
    const chosen = preview.targets.filter(t => !excluded.includes(t.bookingId));
    const chosenCount = excluded.length ? chosen.length : preview.targetCount;

    const send = () => guard(async () => {
        if (!ready) { setErr('날짜와 시간을 넣어 주세요.'); return; }
        if (chosenCount === 0) { setErr('알림을 받을 대기 환자가 없습니다.'); return; }
        setBusy(true); setErr(''); setOk('');
        try {
            const r = await sendClinicBookingOffer({ slot: { day, time }, targets: excluded.length ? chosen.map(t => t.bookingId) : undefined });
            const n = Number.isInteger(r?.targetCount) ? r.targetCount : chosenCount;
            setOk(`대기 중 ${n}명에게 알렸습니다. 먼저 고른 한 분으로 자동 확정되고, ${r?.expiresAt ? `${fmtMs(r.expiresAt)}에` : `응답 시간(${offerWindowMin}분)이 지나면`} 마감됩니다. 확정되면 「일정」 탭에 들어가니 전자차트(EMR)에도 적어 주세요.`);
            setTime('');
            loadOffers();
        } catch (e) { setErr(describeBookingError(e)); }
        finally { setBusy(false); }
    });

    const closeOffer = (o) => guard(async () => {
        setOfferErr(m => ({ ...m, [o.offerId]: '' }));
        try { await closeClinicBookingOffer(o.offerId); loadOffers(); }
        catch (e) { setOfferErr(m => ({ ...m, [o.offerId]: describeBookingError(e) })); }
    });

    const canSend = ready && chosenCount > 0 && !busy;
    return (
        <div>
            <div style={{ ...infoBox, marginBottom: 10 }}>
                {OFFER_FIXED_NOTE}<br />
                {'알림은 빈자리 알림 대기를 신청한 환자에게만 갑니다. 휴대폰 잠금 화면에는 "예약 안내가 있습니다"만 보입니다.'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <input type="date" value={day} min={dayKeyOf()} onChange={(e) => setDay(e.target.value)} style={input} aria-label="빈자리 날짜" />
                <input type="time" step={600} value={time} onChange={(e) => setTime(e.target.value)} style={{ ...input, width: 110 }} aria-label="빈자리 시각" />
                <button type="button" disabled={!canSend} onClick={send} style={{ ...U.buttonPrimary, ...disabledStyle(!canSend) }}>
                    <BellRing size={14} /> {busy ? '보내는 중…' : '빈자리 알리기'}
                </button>
            </div>
            {slotKey && (
                <div style={{ marginTop: 8 }}>
                    {preview.loading || preview.key !== slotKey ? <div style={sub}>{'대기 환자 확인 중…'}</div>
                        : preview.err ? <div style={errBox}>{preview.err}</div>
                        : (
                            <>
                                <div style={{ fontSize: 13.5, color: '#0f172a' }}>
                                    {preview.targetCount === 0 ? '이 시간보다 늦게 잡혀 있거나 아직 확정 전인 대기 환자가 없습니다.' : offerConfirmText(chosenCount)}
                                    {preview.targets.length > 0 && (
                                        <button type="button" onClick={() => setShowNames(v => !v)} style={{ ...smallBtn, marginLeft: 8 }}>{showNames ? '이름 접기' : '이름 보기'}</button>
                                    )}
                                </div>
                                {showNames && preview.targets.map(t => (
                                    <label key={t.bookingId} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '3px 0', color: '#334155' }}>
                                        <input type="checkbox" checked={!excluded.includes(t.bookingId)}
                                            onChange={(e) => setExcluded(list => (e.target.checked ? list.filter(id => id !== t.bookingId) : [...list, t.bookingId]))} />
                                        <span>{t.name || '-'}</span>
                                    </label>
                                ))}
                                {preview.targetCount > 0 && (
                                    <div style={{ ...infoBox, marginTop: 8 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{'환자 앱에 보이는 문구'}</div>
                                        {offerAppText(clinicName, day, time)}
                                    </div>
                                )}
                            </>
                        )}
                </div>
            )}
            {err && <div style={errBox}>{err}</div>}
            {ok && <div style={okBox}>{ok}</div>}

            <div style={{ display: 'flex', alignItems: 'center', margin: '18px 0 6px' }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{'보낸 빈자리'}</h3>
                <button type="button" onClick={loadOffers} style={{ ...smallBtn, marginLeft: 'auto' }}><RefreshCw size={13} /> {'새로고침'}</button>
            </div>
            {offers.loading ? <div style={sub}>{'불러오는 중...'}</div>
                : offers.err ? <div style={errBox}>{offers.err}</div>
                : offers.items.length === 0 ? <div style={sub}>{'보낸 빈자리가 없습니다.'}</div>
                : offers.items.map(o => (
                    <div key={o.offerId} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 4px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{o.slot ? `${fmtDay(o.slot.day)} ${o.slot.time}` : '-'}</span>
                        <span style={tag(o.status === 'open' ? '#eef2ff' : '#f1f5f9', o.status === 'open' ? '#4338ca' : '#475569')}>{offerStatusLabel(o.status)}</span>
                        <span style={sub}>{`${o.targetCount}명에게 보냄`}</span>
                        {o.status === 'open' && o.expiresAt && <span style={sub}>{`${fmtMs(o.expiresAt)} 자동 마감`}</span>}
                        {o.status === 'taken' && o.takenAt && <span style={sub}>{`${fmtMs(o.takenAt)} 선택됨 — 「일정」 탭에 들어갔습니다`}</span>}
                        {o.status === 'open' && (
                            <button type="button" onClick={() => closeOffer(o)} style={{ ...smallBtn, marginLeft: 'auto' }}>{'닫기'}</button>
                        )}
                        {offerErr[o.offerId] && <div style={{ ...errBox, flexBasis: '100%' }}>{offerErr[o.offerId]}</div>}
                    </div>
                ))}
            <div style={{ ...sub, marginTop: 6 }}>{'전화로 자리를 채웠다면 [닫기]를 눌러 주세요.'}</div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 설정 탭 — 대표만 저장(서버 최종 판정). 위탁 확인 새 판 미확인이면 켜기 비활성 + 확인 화면 링크
// ─────────────────────────────────────────────────────────────────────────────
function SettingsTab({ settings, isOwner }) {
    const [draft, setDraft] = useState(settings);
    const [newClosed, setNewClosed] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [ok, setOk] = useState('');
    const guard = useInFlight();
    useEffect(() => { setDraft(settings); }, [settings]);
    if (!draft) return null;

    const ro = !isOwner || busy;
    const set = (patch) => { setDraft(d => ({ ...d, ...patch })); setOk(''); };
    const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v].sort());
    const save = () => guard(async () => {
        if (draft.enabled && draft.days.length === 0) { setErr('받는 요일을 하나 이상 골라 주세요.'); return; }
        if (draft.enabled && draft.parts.length === 0) { setErr('오전·오후 중 하나 이상 골라 주세요.'); return; }
        const cap = Number(draft.dailyCap);
        if (!Number.isInteger(cap) || cap < 1 || cap > DAILY_CAP_MAX) { setErr(`하루 상한은 1~${DAILY_CAP_MAX}건으로 넣어 주세요.`); return; }
        setBusy(true); setErr(''); setOk('');
        try {
            await setClinicBookingSettings({
                enabled: draft.enabled === true, days: draft.days, parts: draft.parts, dailyCap: cap,
                offerWindowMin: draft.offerWindowMin, closedDays: draft.closedDays, notifyNew: draft.notifyNew === true,
            });
            setOk('저장했습니다.');
        } catch (e) { setErr(describeBookingError(e)); }
        finally { setBusy(false); }
    });

    const row = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid #f1f5f9' };
    const lab = { fontSize: 13, fontWeight: 600, color: '#334155', minWidth: 120 };
    return (
        <div>
            {!isOwner && <div style={{ ...infoBox, marginBottom: 8 }}>{OWNER_ONLY_TEXT}</div>}
            <div style={row}>
                <span style={lab}>{'예약 받기'}</span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, ...disabledStyle(ro || (!draft.paClinicReady && !draft.enabled)) }}>
                    <input type="checkbox" checked={draft.enabled} disabled={ro || (!draft.paClinicReady && !draft.enabled)} onChange={(e) => set({ enabled: e.target.checked })} />
                    {draft.enabled ? '켜짐 — 환자 앱에서 예약 요청을 받습니다' : '꺼짐'}
                </label>
                {!draft.paClinicReady && (
                    <span style={{ fontSize: 12.5, color: '#b45309' }}>
                        {`${SETTINGS_LOCK_NOTE} `}
                        <Link to="/settings/clinic-delegation">{'처리위탁 확인으로 가기'}</Link>
                    </span>
                )}
            </div>
            <div style={row}>
                <span style={lab}>{'받는 요일'}</span>
                {[1, 2, 3, 4, 5, 6, 0].map(n => (
                    <button key={n} type="button" disabled={ro} onClick={() => set({ days: toggleIn(draft.days, n) })} style={{ ...chipBtn(draft.days.includes(n)), ...disabledStyle(ro) }}>{WEEKDAYS[n]}</button>
                ))}
            </div>
            <div style={row}>
                <span style={lab}>{'오전·오후'}</span>
                {['am', 'pm'].map(p => (
                    <button key={p} type="button" disabled={ro} onClick={() => set({ parts: toggleIn(draft.parts, p) })} style={{ ...chipBtn(draft.parts.includes(p)), ...disabledStyle(ro) }}>{partLabel(p)}</button>
                ))}
            </div>
            <div style={row}>
                <span style={lab}>{'하루 상한'}</span>
                <input type="number" min={1} max={DAILY_CAP_MAX} value={draft.dailyCap} disabled={ro} onChange={(e) => set({ dailyCap: e.target.value === '' ? '' : Number(e.target.value) })} style={{ ...input, width: 80 }} />
                <span style={sub}>{'건 (넘으면 그날은 환자 앱에서 요청을 받지 않습니다)'}</span>
            </div>
            <div style={row}>
                <span style={lab}>{'빈자리 응답 시간'}</span>
                {OFFER_WINDOW_OPTIONS.map(m => (
                    <button key={m} type="button" disabled={ro} onClick={() => set({ offerWindowMin: m })} style={{ ...chipBtn(draft.offerWindowMin === m), ...disabledStyle(ro) }}>{`${m}분`}</button>
                ))}
                <span style={sub}>{'빈자리 시각 30분 전에는 자동으로 마감됩니다.'}</span>
            </div>
            <div style={row}>
                <span style={lab}>{'휴진일'}</span>
                <input type="date" value={newClosed} min={dayKeyOf()} disabled={ro} onChange={(e) => setNewClosed(e.target.value)} style={input} />
                <button type="button" disabled={ro || !isDayKey(newClosed) || draft.closedDays.includes(newClosed)}
                    onClick={() => { set({ closedDays: [...draft.closedDays, newClosed].sort() }); setNewClosed(''); }} style={smallBtn}>{'추가'}</button>
                {draft.closedDays.map(d => (
                    <span key={d} style={{ ...tag('#f1f5f9', '#334155'), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {fmtDay(d)}
                        {!ro && <button type="button" onClick={() => set({ closedDays: draft.closedDays.filter(x => x !== d) })} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', padding: 0 }} aria-label="휴진일 빼기">{'×'}</button>}
                    </span>
                ))}
                {(draft.roadmapClosedDays.length > 0 || draft.roadmapClosedWeekly.length > 0) && (
                    <div style={{ ...sub, flexBasis: '100%' }}>
                        {`진료 후 질문 설정의 휴진도 함께 적용됩니다: ${[...draft.roadmapClosedWeekly.map(n => `매주 ${WEEKDAYS[n]}`), ...draft.roadmapClosedDays.map(fmtDay)].join(', ')}`}
                    </div>
                )}
            </div>
            <div style={row}>
                <span style={lab}>{'새 요청 알림'}</span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={draft.notifyNew} disabled={ro} onChange={(e) => set({ notifyNew: e.target.checked })} />
                    {'새 요청이 오면 이 웹으로 알림 받기(30분에 한 번으로 묶음, 환자 이름 없음)'}
                </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                <button type="button" disabled={ro} onClick={save} style={{ ...U.buttonPrimary, ...disabledStyle(ro) }}>{busy ? '저장 중…' : '저장'}</button>
            </div>
            {err && <div style={errBox}>{err}</div>}
            {ok && <div style={okBox}>{ok}</div>}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
function ClinicBookings({ user }) {
    const gate = useBookingAvailable(user);
    const [tab, setTab] = useState('requests');
    const [counts, setCounts] = useState({ newCount: 0, waitlistCount: 0 });
    const on = gate.on;

    const loadCount = useCallback(() => {
        if (!on) return;
        getClinicBookingInboxCount()
            .then(setCounts)
            .catch(e => console.error('[예약] 건수 조회 실패:', e?.code || e?.details?.code || e?.message || e));
    }, [on]);
    useEffect(() => { loadCount(); }, [loadCount]);

    const settings = gate.settings;
    const isOwner = settings?.isOwner === true || !user?.companyId;
    const clinicName = settings?.clinicName || user?.companyName || user?.businessName || '본원';

    const TABS = [
        { key: 'requests', label: counts.newCount > 0 ? `요청 (새 ${counts.newCount})` : '요청', icon: Inbox },
        { key: 'schedule', label: '일정', icon: CalendarDays },
        { key: 'offer', label: counts.waitlistCount > 0 ? `빈자리 (대기 ${counts.waitlistCount})` : '빈자리', icon: BellRing },
        { key: 'settings', label: '설정', icon: SettingsIcon },
    ];

    return (
        <div style={U.content}>
            <PageHeader icon={CalendarCheck} title="예약" subtitle={BOOKING_HEAD_NOTE}
                actions={<Link to="/patients" style={{ ...U.buttonSecondary, textDecoration: 'none' }}><ArrowLeft size={14} /> {'환자 자동관리'}</Link>} />
            {gate.loading ? (
                <div style={{ ...U.emptyState, padding: '40px 0' }}>{'불러오는 중...'}</div>
            ) : !on ? (
                <div style={U.emptyState}>{gate.flagOn && !settings ? '예약 설정을 불러오지 못했습니다. 잠시 후 새로고침해 주세요.' : '예약 기능이 아직 열리지 않았습니다.'}</div>
            ) : (
                <div style={{ paddingTop: 12 }}>
                    {!settings.enabled && (
                        <div style={{ ...infoBox, marginBottom: 10 }}>
                            {'예약 받기가 꺼져 있어 환자 앱에서 새 요청을 받지 않습니다. '}
                            {isOwner ? <button type="button" onClick={() => setTab('settings')} style={{ ...smallBtn, marginLeft: 4 }}>{'설정에서 켜기'}</button> : '치과 대표가 설정에서 켤 수 있습니다.'}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                        {TABS.map(t => {
                            const Icon = t.icon;
                            return <button key={t.key} type="button" onClick={() => setTab(t.key)} style={chipBtn(tab === t.key)}><Icon size={14} /> {t.label}</button>;
                        })}
                    </div>
                    <div style={{ ...U.card }}>
                        {tab === 'requests' && <RequestsTab onCountChange={loadCount} />}
                        {tab === 'schedule' && <ScheduleTab />}
                        {tab === 'offer' && <OfferTab clinicName={clinicName} offerWindowMin={settings.offerWindowMin} />}
                        {tab === 'settings' && <SettingsTab settings={settings} isOwner={isOwner} />}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ClinicBookings;
